import { RootState } from '@Redux/store'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { sendUpdateWatchlistOrdersConnection } from '@Redux/slices/sockets/ProxyServiceConnectionSlice'

import type { PayloadAction } from '@reduxjs/toolkit'

export type WatchlistItem = {
  key: string
  name: string
  icon?: string | React.ReactNode
  value?: string
  isSearchWord?: boolean
  subfilters?: boolean | WatchlistItem[]
  isCustom?: boolean
}

export type Watchlist = {
  name: string
  key: string
  subtitle?: string
  icon?: string | React.ReactNode
  items: WatchlistItem[]
  is_enabled: boolean
  isCustom: boolean
  order: number
  market_ids: number[]
}

export type WatchlistMarket = {
  id: number
  source: string
  source_name: string
  target: string
  target_name: string
  title: string
}

export type WatchlistsState = {
  watchlists: Record<string, Watchlist>
  order: string[]
  activeWatchlistId: string | null
  selectedTab: string | null
  favouriteMarketIds: number[]
}

export const defaultWatchlistKeys = [
  'portfolio',
  'most_traded',
  'popular',
  'top_risers',
  'top_fallers',
  'most_volatile',
  'commods',
  // "oil_markets",
  'indexes',
  'interest_rates',
  'bonds',
  'fiat',
  'crypto',
  'stocks',
  // "etf",
]

const defaultWatchlists: Watchlist[] = [
  {
    name: 'Favourites',
    key: 'favourites',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 0,
    market_ids: [],
  },
  {
    name: 'Portfolio',
    key: 'portfolio',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 1,
    market_ids: [],
  },
  {
    name: 'Cryptos',
    key: 'crypto',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 2,
    market_ids: [],
  },
  // {
  //   name: 'ETF',
  //   key: 'etf',
  //   items: [],
  //   is_enabled: true,
  //   isCustom: false,
  //   order: 3,
  //   market_ids: [],
  // },
  {
    name: 'Most traded',
    key: 'most_traded',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 4,
    market_ids: [],
  },
  {
    name: 'Indices',
    key: 'indexes',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 5,
    market_ids: [],
  },
  {
    name: 'Top risers',
    key: 'top_risers',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 6,
    market_ids: [],
  },
  {
    name: 'Top fallers',
    key: 'top_fallers',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 7,
    market_ids: [],
  },
  {
    name: 'Most volatile',
    key: 'most_volatile',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 8,
    market_ids: [],
  },
  // {
  //   name: 'Oil markets',
  //   key: 'oil_markets',
  //   items: [],
  //   is_enabled: true,
  //   isCustom: false,
  //   order: 9,
  //   market_ids: [],
  // },
  {
    name: 'Commodities',
    key: 'commods',
    items: [
      { key: 'base_metals', name: 'Base metals', value: 'base_metals' },
      { key: 'energies', name: 'Energies', value: 'energies' },
      { key: 'precious_metals', name: 'Precious metals', value: 'precious_metals' },
      { key: 'soft_commodities', name: 'Soft commodities', value: 'soft_commodities' },
      { key: 'commodity_futures', name: 'Commodity futures', value: 'commodity_futures' },
    ],
    is_enabled: true,
    isCustom: false,
    order: 10,
    market_ids: [],
  },
  {
    name: 'Forex',
    key: 'fiat',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 11,
    market_ids: [],
  },
  {
    name: 'Shares',
    key: 'stocks',
    items: [],
    is_enabled: true,
    isCustom: false,
    order: 12,
    market_ids: [],
  },
]

const initialState: WatchlistsState = {
  watchlists: defaultWatchlists.reduce((acc, watchlist) => {
    acc[watchlist.key] = watchlist
    return acc
  }, {} as Record<string, Watchlist>),
  order: defaultWatchlists.map((w) => w.key),
  activeWatchlistId: null,
  selectedTab: null,
}

const watchlistsSlice = createSlice({
  name: 'watchlists',
  initialState: initialState,
  reducers: {
    reorderWatchlists: (state, action: PayloadAction<string[]>) => {
      state.order = action.payload

      action.payload.forEach((id, index) => {
        if (state.watchlists[id]) {
          state.watchlists[id].order = index
        }
      })
    },

    moveMarket: (state, action: PayloadAction<{watchlistId: string, marketId: number, order: number}>) => {
      const watchlist = state.watchlists[action.payload.watchlistId]

      if (! watchlist) {
        return
      }

      var marketIds = state.watchlists[action.payload.watchlistId]?.market_ids ?? []

      if (action.payload.order < 0) {
        action.payload.order = 0;
      }

      marketIds = marketIds.filter(
        marketId => marketId !== action.payload.marketId
      );

      const maxOrder = marketIds.length;

      if (action.payload.order > maxOrder) {
        action.payload.order = maxOrder;
      }

      marketIds.splice(action.payload.order, 0, action.payload.marketId)

      state.watchlists[action.payload.watchlistId].market_ids = marketIds
    },

    setSelectedTab: (state, action: PayloadAction<string | null>) => {
      state.selectedTab = action.payload
    },

    setActiveWatchlist: (state, action: PayloadAction<string | null>) => {
      state.activeWatchlistId = action.payload
    },

    updateWatchlistItems: (state, action: PayloadAction<{ watchlistId: string; items: WatchlistItem[] }>) => {
      const watchlist = state.watchlists[action.payload.watchlistId]
      if (watchlist) {
        watchlist.items = action.payload.items
      }
    },

    resetToDefaults: (state) => {
      state.watchlists = defaultWatchlists.reduce((acc, watchlist) => {
        acc[watchlist.key] = watchlist
        return acc
      }, {} as Record<string, Watchlist>)
      state.order = defaultWatchlists.map((w) => w.key)
      state.activeWatchlistId = null
      state.selectedTab = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setWatchlists.fulfilled, (state, action: PayloadAction<{watchlists: Watchlist[]}>) => {
        if (!action?.payload?.watchlists?.length) return

        const responseWatchlist = action?.payload?.watchlists?.reduce((acc, watchlist: any) => {
          const marketIds = Array.isArray(watchlist.markets)
            ? watchlist.markets.map((m: WatchlistMarket) => m.id)
            : (watchlist.market_ids ?? [])

          acc[watchlist.key] = {
            ...watchlist,
            market_ids: marketIds,
            isCustom: !defaultWatchlistKeys.includes(watchlist.key),
          }

          if (watchlist.key === 'favourites') {
            state.favouriteMarketIds = marketIds
          }

          return acc
        }, {} as Record<string, Watchlist>)

        const keys = [...new Set([...Object.keys(state.watchlists), ...Object.keys(responseWatchlist)])]

        const watchlist = keys.reduce((acc, key) => {
          if (defaultWatchlistKeys.includes(key) || !!responseWatchlist?.[key]) {
            acc.push({...state.watchlists?.[key], ...(responseWatchlist?.[key] ?? state.watchlists?.[key])})
          }

          return acc
        }, [] as Watchlist[])
        ?.sort((a, b) => a.order - b.order)

      state.watchlists =
        watchlist?.reduce((acc, watchlist) => {
          acc[watchlist.key] = watchlist
          return acc
        }, {} as Record<string, Watchlist>) ?? {}

      state.order = watchlist?.map((w) => w.key) ?? []
    })
  },
})

export const setWatchlists = createAsyncThunk('watchlist/setWatchlists', async (watchlists: Watchlist[]) => {
  return { watchlists }
})

export const createWatchlists = createAsyncThunk(
  'watchlist/createWatchlists',
  async (key: string, { dispatch, getState }) => {
    const state = getState() as RootState
    dispatch(sendUpdateWatchlistOrdersConnection([...state.watchlists.order].splice(1, 0, key)))
  },
)

export const deleteWatchlists = createAsyncThunk(
  'watchlist/createWatchlists',
  async (key: string, { dispatch, getState }) => {
    const state = getState() as RootState
    dispatch(sendUpdateWatchlistOrdersConnection(state.watchlists.order.filter((item) => item !== key)))
  },
)

export const { reorderWatchlists, moveMarket, setSelectedTab, setActiveWatchlist, updateWatchlistItems, resetToDefaults } =
  watchlistsSlice.actions

export default watchlistsSlice.reducer
